import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { AuthService } from '@services/auth.service';
import { BreakpointService } from '@services/breakpoint.service';

import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ScopeServer } from '@app/models/scope-server';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { LaunchDialogComponent } from '../launch-dialog/launch-dialog.component';

/**
 * scope card component
 *
 * consists of two states: either logged-in or not
 * when not logged it shows a login button
 * otherwise a button to launch scope client is shown
 * @class ScopeCardComponent
 * @typedef {ScopeCardComponent}
 */
@Component( {
    selector: 'app-scope-card',
    imports: [ CommonModule, MatCardModule, MatIconModule, MatDialogModule, MatButtonModule, NgTemplateOutlet, DebounceClickDirective, MatFormFieldModule, MatSelectModule ],
    templateUrl: './scope-card.component.html',
    styleUrl: './scope-card.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
} )
export class ScopeCardComponent {
    authService = inject( AuthService );
    #dialog = inject( MatDialog );
    #breakpointService = inject( BreakpointService );

    isLoggedIn = computed( () => this.authService.isLoggedIn() );
    loginDisabled = computed( () => this.authService.loginDisabled() );

    isMobile = this.#breakpointService.isMobile;
    isTabletPortrait = this.#breakpointService.isTabletPortrait;

    readonly currentUser = this.authService.currentUser;

    AVATAR_PLACEHOLDER = 'avatar_placeholder';
    avatarIcon = computed( () => this.currentUser()?.avatarURL ?? this.AVATAR_PLACEHOLDER );

    today = new Date();
    greeting = this.#getGreeting();

    #getGreeting(): string {
        const hour = new Date().getHours();
        if ( hour < 12 ) return $localize`:@@welcome-card-greeting-morning:Good Morning!`;
        if ( hour < 18 ) return $localize`:@@welcome-card-greeting-day:Good Day!`;
        return $localize`:@@welcome-card-greeting-evening:Good Evening!`;
    }

    /**
     * log in helper function
     * calls login in {@link AuthService}
     */
    login() {
        this.authService.login();
    }

    /** reference on scope servers, assigned to users organisation */
    servers = computed<ScopeServer[] | null>( () => this.authService.servers() );
    selectedServer = computed<ScopeServer | null>( () => this.authService.selectedServer() );

    /**
     * helper function to open {@link LaunchDialogComponent} modal
     * with required parameters to start Scope client
     * @returns void
     * @throws Error when no server is selected
     * @see {@link LaunchDialogComponent}
     */
    launchScope() {
        this.#dialog.open(
            LaunchDialogComponent,
            {
                data: {
                    version: this.selectedServer()?.scopeVersion,
                    url: this.selectedServer()?.startUrl,
                }
            }
        );
    }
}
